import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronDown, Download } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { ReportDetailPanel } from "./propertyReportGallery/ReportDetailPanel";
import { ReportGallerySkeleton } from "./propertyReportGallery/ReportGallerySkeleton";
import { ReportPhotoViewer } from "./propertyReportGallery/ReportPhotoViewer";
import { ReportTreeNav } from "./propertyReportGallery/ReportTreeNav";
import type { ReportGalleryItem } from "./propertyReportGallery/types";

export type {
  ReportGalleryDetail,
  ReportGalleryItem,
  ReportGalleryPhoto,
} from "./propertyReportGallery/types";

interface PropertyReportGalleryViewProps {
  items: ReportGalleryItem[];
  loading: boolean;
  error?: Error | null;
  onRetry?: () => void;
  searchPlaceholder: string;
  emptyNavMessage: string;
  detailTitle: string;
  detailEmptyMessage: string;
  downloadAllLabel?: string;
  onDownloadAll?: () => void;
  downloadAllDisabled?: boolean;
  downloadAllOptions?: Array<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }>;
  onDownloadCategory?: (category: string, itemsInCategory: ReportGalleryItem[]) => void;
  focusedItemId?: string | null;
  summary?: ReactNode;
  showItemsWithoutPhotos?: boolean;
}

export function PropertyReportGalleryView({
  items,
  loading,
  error,
  onRetry,
  searchPlaceholder,
  emptyNavMessage,
  detailTitle,
  detailEmptyMessage,
  downloadAllLabel,
  onDownloadAll,
  downloadAllDisabled,
  downloadAllOptions,
  onDownloadCategory,
  focusedItemId,
  summary,
  showItemsWithoutPhotos = false,
}: PropertyReportGalleryViewProps) {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [didSeedDefaults, setDidSeedDefaults] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const selectedItem = useMemo(
    () => items.find((item) => item.id === selectedItemId) ?? null,
    [items, selectedItemId]
  );

  const focusedItem = useMemo(
    () => items.find((item) => item.id === focusedItemId) ?? null,
    [focusedItemId, items]
  );

  useEffect(() => {
    if (loading || !focusedItem) return;
    setSelectedItemId(focusedItem.id);
    setPhotoIndex(0);
    setExpandedCategories((prev) => new Set([...prev, focusedItem.category]));
    setDidSeedDefaults(true);
  }, [focusedItem, loading]);

  useEffect(() => {
    if (didSeedDefaults || loading || items.length === 0 || focusedItem) return;
    const byCategory = new Map<string, ReportGalleryItem[]>();
    for (const item of items) {
      if (!showItemsWithoutPhotos && item.photos.length === 0) continue;
      const list = byCategory.get(item.category) ?? [];
      list.push(item);
      byCategory.set(item.category, list);
    }
    const firstCategory = Array.from(byCategory.entries())
      .map(([category, categoryItems]) => ({
        category,
        order: Math.min(...categoryItems.map((item) => item.categoryOrder ?? 999)),
      }))
      .sort((a, b) => a.order - b.order || a.category.localeCompare(b.category))[0]?.category;
    if (!firstCategory) {
      setDidSeedDefaults(true);
      return;
    }
    const firstItem = (byCategory.get(firstCategory) ?? [])
      .slice()
      .sort((x, y) => x.name.localeCompare(y.name))[0];
    setExpandedCategories(new Set([firstCategory]));
    if (firstItem) {
      setSelectedItemId(firstItem.id);
      setPhotoIndex(0);
    }
    setDidSeedDefaults(true);
  }, [items, loading, didSeedDefaults, focusedItem, showItemsWithoutPhotos]);

  const totalPhotoCount = useMemo(
    () => items.reduce((n, item) => n + item.photos.length, 0),
    [items]
  );

  const toggleCategory = (cat: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  const selectItem = (id: string) => {
    setSelectedItemId(id);
    setPhotoIndex(0);
  };

  if (loading) {
    return <ReportGallerySkeleton />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[600px] gap-3">
        <p className="text-sm text-destructive">{error.message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="text-sm text-primary hover:underline"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-244px)] min-h-[600px] flex-col gap-3">
      {summary ? <div className="shrink-0">{summary}</div> : null}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row gap-0 border border-border rounded-lg overflow-hidden bg-card">
        <div className="md:w-[280px] border-b md:border-b-0 md:border-r border-border overflow-y-auto p-4 space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Report Gallery
          </p>
          <Input
            type="search"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {downloadAllLabel && downloadAllOptions && downloadAllOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full gap-2"
                  disabled={downloadAllDisabled ?? totalPhotoCount === 0}
                >
                  <Download className="h-4 w-4" />
                  {downloadAllLabel}
                  <ChevronDown className="h-4 w-4 ml-auto" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
                {downloadAllOptions.map((option) => (
                  <DropdownMenuItem
                    key={option.label}
                    disabled={option.disabled}
                    onClick={option.onClick}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {onDownloadAll && downloadAllLabel && !downloadAllOptions?.length && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full gap-2"
              disabled={downloadAllDisabled ?? totalPhotoCount === 0}
              onClick={onDownloadAll}
            >
              <Download className="h-4 w-4" />
              {downloadAllLabel}
            </Button>
          )}
          <ReportTreeNav
            items={items}
            selectedItemId={selectedItemId}
            expanded={expandedCategories}
            onToggleCategory={toggleCategory}
            onSelectItem={selectItem}
            onDownloadCategory={onDownloadCategory}
            query={searchQuery}
            emptyMessage={emptyNavMessage}
            showItemsWithoutPhotos={showItemsWithoutPhotos}
          />
        </div>

        <div
          tabIndex={0}
          className="flex-1 flex flex-col bg-background overflow-hidden outline-none"
          onKeyDown={(e) => {
            if (!selectedItem) return;
            const photos = selectedItem.photos;
            if (photos.length === 0) return;
            if (e.key === "ArrowLeft") {
              e.preventDefault();
              setPhotoIndex((i) => Math.max(0, i - 1));
            } else if (e.key === "ArrowRight") {
              e.preventDefault();
              setPhotoIndex((i) => Math.min(photos.length - 1, i + 1));
            } else if (e.key === "Escape") {
              e.preventDefault();
              setSelectedItemId(null);
            }
          }}
        >
          <ReportPhotoViewer
            item={selectedItem}
            photoIndex={photoIndex}
            onIndexChange={setPhotoIndex}
          />
        </div>

        <div className="md:w-[320px] border-t md:border-t-0 md:border-l border-border overflow-y-auto p-4">
          <ReportDetailPanel
            item={selectedItem}
            title={detailTitle}
            emptyMessage={detailEmptyMessage}
          />
        </div>
      </div>
    </div>
  );
}
